import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useUrlTableState from '../../../hooks/useUrlTableState';
import { useAuth } from '../../../context/AuthContext';
import PermissionGate from '../../../components/common/PermissionGate';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  Calendar,
  Clock,
  MapPin,
  Video,
  Edit,
  Trash2,
  Eye,
  XCircle,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X,
  CheckCircle,
  Square,
  List,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Send,
  Bell,
  Users,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import Button from '../../../components/common/Button/Button';
import interviewService from '../../../api/services/interviewService';
import candidateService from '../../../api/services/candidateService';
import ScheduleInterviewModal from '../../../components/recruitment/ScheduleInterviewModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
const BACKEND_FETCH_LIMIT = 500;
const EMPTY_ARRAY = [];

const normalizeDateValue = (value) => {
  if (!value) return "";
  try {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split("T")[0];
    }
  } catch (_) { }
  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[0];
  }
  return typeof value === "string" ? value : "";
};

const normalizeTimeValue = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 5);
  }
  return typeof value === "string" ? value : "";
};

const formatTimeAmPm = (timeStr) => {
  if (!timeStr) return "N/A";
  if (!timeStr.includes(':')) return timeStr;
  const [hours, minutes] = timeStr.split(':');
  const h = parseInt(hours, 10);
  if (isNaN(h)) return timeStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hr12 = h % 12 || 12;
  return `${hr12}:${minutes.substring(0, 2)} ${ampm}`;
};


const InterviewScheduling = ({ isEmployeeView = false }) => {
  const navigate = useNavigate();

  const { user } = useAuth();

  // ── URL-persisted filter state ──────────────────────────────────────────────
  const defaultFilters = useMemo(() => ({
    search: '',
    status: 'all',
    type: 'all',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    viewMode: 'list',
  }), []);

  const {
    filters: interviewFilters,
    searchTerm: urlSearchTerm,
    setSearchTerm: setUrlSearchTerm,
    updateFilter: updateInterviewFilter,
    updatePage: updateInterviewPage,
    updateLimit: updateInterviewLimit,
    updateSort: updateInterviewSort,
    resetFilters: resetInterviewFilters,
  } = useUrlTableState(defaultFilters, { searchKey: 'search' });

  // Derive individual filter values from URL state
  const searchTerm = interviewFilters.search || '';
  const [searchInputValue, setSearchInputValue] = useState(() => interviewFilters.search || '');

  const filterStatus = interviewFilters.status || 'all';
  const setFilterStatus = useCallback((status) => {
    updateInterviewFilter({ status });
  }, [updateInterviewFilter]);

  const filterType = interviewFilters.type || 'all';
  const setFilterType = useCallback((type) => {
    updateInterviewFilter({ type });
  }, [updateInterviewFilter]);

  const dateRange = useMemo(() => ({
    startDate: interviewFilters.startDate || '',
    endDate: interviewFilters.endDate || '',
  }), [interviewFilters.startDate, interviewFilters.endDate]);
  const setDateRange = useCallback((range) => {
    updateInterviewFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateInterviewFilter]);

  const sortConfig = useMemo(() => ({
    key: interviewFilters.sortBy || 'createdAt',
    direction: interviewFilters.sortOrder || 'desc',
  }), [interviewFilters.sortBy, interviewFilters.sortOrder]);

  const page = interviewFilters.page || 1;
  const setPage = useCallback((p) => {
    updateInterviewPage(p);
  }, [updateInterviewPage]);

  const pageSize = interviewFilters.limit || 10;
  const setPageSize = useCallback((limit) => {
    updateInterviewLimit(limit);
  }, [updateInterviewLimit]);

  const viewMode = interviewFilters.viewMode || 'list';
  const setViewMode = useCallback((mode) => {
    updateInterviewFilter({ viewMode: mode });
  }, [updateInterviewFilter]);

  // Sync local search input from URL on mount / browser back-forward
  useEffect(() => {
    setSearchInputValue(urlSearchTerm || '');
  }, [urlSearchTerm]);
  // ────────────────────────────────────────────────────────────────────────────

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editInterviewData, setEditInterviewData] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const [interviewToDelete, setInterviewToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Completion Remark State
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkContent, setRemarkContent] = useState('');
  const [remarkOutcome, setRemarkOutcome] = useState('selected'); // 'selected' | 'rejected'
  const [interviewIdForRemark, setInterviewIdForRemark] = useState(null);

  // Reminder Confirmation State
  const [showReminderConfirm, setShowReminderConfirm] = useState(false);
  const [interviewIdForReminder, setInterviewIdForReminder] = useState(null);

  const [showRoundPicker, setShowRoundPicker] = useState(false);
  const [roundPickerData, setRoundPickerData] = useState(null);

  const [showInterviewDetails, setShowInterviewDetails] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  const {
    data: interviewsData,
    isLoading: loadingInterviews,
    error: interviewError,
    refetch: refetchInterviews
  } = useQuery({
    queryKey: ['interviews', searchTerm, filterStatus, filterType, dateRange, page, pageSize, isEmployeeView, user?.id || user?._id],
    queryFn: async () => {
      const mergedParams = {
        page,
        limit: pageSize,
        sortBy: sortConfig.key === 'date' ? 'scheduledDate' : sortConfig.key,
        sortOrder: sortConfig.direction,
        ...(isEmployeeView && (user?.id || user?._id) ? { interviewer: user?.id || user?._id } : {}),
        ...(filterStatus !== 'all' ? { status: filterStatus } : {}),
        ...(filterType !== 'all' ? { interviewType: filterType } : {}),
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(dateRange.startDate ? { startDate: dateRange.startDate } : {}),
        ...(dateRange.endDate ? { endDate: dateRange.endDate } : {}),
      };
      const result = await interviewService.getInterviews(mergedParams);

      let rawInterviews = [];
      if (result?.data?.interviews) rawInterviews = result.data.interviews;
      else if (result?.interviews) rawInterviews = result.interviews;
      else if (Array.isArray(result)) rawInterviews = result;

      let mappedData = rawInterviews.map((interview) => {
        const creator = interview.createdBy || interview.creator;

        // Prioritize backend's already resolved name, email, and department
        const creatorName = interview.createdByName ||
          creator?.fullName || creator?.name ||
          (creator?.personalInfo?.firstName ? `${creator.personalInfo.firstName} ${creator.personalInfo.lastName || ""}`.trim() : null) ||
          (typeof creator === 'string' && creator.match(/^[0-9a-fA-F]{24}$/) ? `User (ID: ${creator.substring(0, 8)}...)` : (typeof creator === 'string' ? creator : "N/A"));

        const creatorEmail = interview.creatorEmail || creator?.email || creator?.personalInfo?.email || "";
        const creatorDepartment = interview.creatorDepartment || creator?.department || "";

        return {
          id: interview.id || interview._id,
          interviewId: interview.interviewId || `INT${Date.now()}`,
          candidate: interview.candidate,
          candidateName: interview.candidate?.name || (interview.candidate ? `${interview.candidate.firstName || ''} ${interview.candidate.lastName || ''}`.trim() : "Unknown"),
          candidateEmail: interview.candidate?.email || "",
          job: interview.job,
          jobTitle: interview.job?.title || "Unknown Job",
          jobId: interview.job?.id || interview.job?._id || "unknown",
          interviewer: interview.interviewer,
          interviewers: interview.interviewers || (interview.interviewer ? [interview.interviewer] : []),
          interviewerName: (interview.interviewers && interview.interviewers.length > 0)
            ? interview.interviewers.map(i => i.name).filter(Boolean).join(', ')
            : (interview.interviewer?.name || "Unknown"),
          interviewerEmail: (interview.interviewers && interview.interviewers.length > 0)
            ? interview.interviewers.map(i => i.email).filter(Boolean).join(', ')
            : (interview.interviewer?.email || ""),
          scheduledAt: interview.scheduledAt || interview.scheduledDate || interview.date,
          date: normalizeDateValue(interview.scheduledAt || interview.scheduledDate || interview.date),
          time: normalizeTimeValue(interview.scheduledAt || interview.scheduledDate || interview.time),
          duration: interview.duration || 60,
          type: interview.type || interview.interviewType || "video",
          status: (interview.status || "scheduled").toLowerCase(),
          location: interview.location || "",
          meetingLink: interview.meetingLink || "",
          notes: interview.notes || "",
          rounds: interview.rounds || [],
          completionRemark: interview.completionRemark || null,
          createdAt: interview.createdAt || new Date().toISOString(),
          createdByName: creatorName,
          creatorEmail: creatorEmail,
          creatorDepartment: creatorDepartment,
        };
      });

      return {
        items: mappedData,
        pagination: result?.data?.pagination || result?.pagination || null
      };
    },
    staleTime: 0,
  });

  // Auto-trigger edit modal from URL params
  useEffect(() => {
    const action = searchParams.get('action');
    const id = searchParams.get('id');
    const roundId = searchParams.get('roundId');
    const interviews = interviewsData?.items || [];

    if (action === 'edit' && id && interviews.length > 0) {
      const interviewToEdit = interviews.find(i => i.id === id);
      if (interviewToEdit) {
        let finalEditData = { ...interviewToEdit };

        // If specific round requested, pre-fill modal with that round's data
        if (roundId && interviewToEdit.rounds) {
          const specificRound = interviewToEdit.rounds.find(r => (r.id || r._id) === roundId);
          if (specificRound) {
            finalEditData = {
              ...finalEditData,
              roundType: specificRound.roundType,
              interviewer: specificRound.interviewer,
              interviewerId: specificRound.interviewer?.id || specificRound.interviewer?._id,
              interviewerName: specificRound.interviewer?.name,
              interviewerEmail: specificRound.interviewer?.email,
              scheduledAt: specificRound.scheduledAt,
              duration: specificRound.duration,
              type: specificRound.type || specificRound.interviewType,
              location: specificRound.location,
              meetingLink: specificRound.meetingLink,
              notes: specificRound.notes || specificRound.extraNote,
            };
          }
        }

        setEditInterviewData(finalEditData);
        setShowScheduleForm(true);
        // Clear params to prevent re-opening on manual refreshes
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.delete('action');
          next.delete('id');
          next.delete('roundId');
          return next;
        }, { replace: true });
      }
    }
  }, [searchParams, interviewsData, setSearchParams]);

  // React Query: Fetch Shortlisted Candidates
  const { data: candidatesData, isLoading: loadingCandidates } = useQuery({
    queryKey: ['shortlisted-candidates'],
    queryFn: async () => {
      const result = await candidateService.getShortlistedCandidates();
      let candidatesData = [];
      if (result?.data?.candidates) candidatesData = result.data.candidates;
      else if (result?.data?.data?.candidates) candidatesData = result.data.data.candidates;
      else if (result?.candidates) candidatesData = result.candidates;
      else if (Array.isArray(result)) candidatesData = result;
      else if (result?.data && Array.isArray(result.data)) candidatesData = result.data;

      return candidatesData
        .filter(c => ['shortlisted', 'interview'].includes((c.recruitmentStatus || c.status || "").toLowerCase()))
        .map(candidate => ({
          id: candidate.id || candidate._id,
          name: candidate.name || `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() || "Unknown",
          email: candidate.email || "",
          jobTitle: candidate.jobTitle || "Unknown Job",
          jobId: candidate.jobId || candidate.job || "unknown",
          status: candidate.status || candidate.recruitmentStatus || "shortlisted",
        }));
    },
    enabled: showScheduleForm && !isEmployeeView && user?.role !== 'employee'
  });
  const candidates = candidatesData || EMPTY_ARRAY;

  // React Query: Update Status Mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, ...additionalData }) => interviewService.updateInterviewStatus(id, status, additionalData),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success(`Interview status updated to ${variables.status}`);
    },
    onError: (err) => {
      toast.error('Failed to update status: ' + (err.message || 'Unknown error'));
    }
  });

  // React Query: Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => interviewService.deleteInterview(id),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Interview deleted successfully!');
      setShowDeleteConfirm(false);
      setInterviewToDelete(null);
    },
    onError: (err) => {
      toast.error('Failed to delete interview: ' + (err.message || 'Unknown error'));
    }
  });

  // React Query: Send Reminder Mutation
  const sendReminderMutation = useMutation({
    mutationFn: (id) => interviewService.sendInterviewReminder(id),
    onSuccess: (result) => {
      toast.success('Interview reminder sent successfully!');
    },
    onError: (err) => {
      toast.error('Failed to send reminder: ' + (err.message || 'Unknown error'));
    }
  });

  const handleSendReminder = (id) => {
    setInterviewIdForReminder(id);
    setShowReminderConfirm(true);
  };

  const confirmSendReminder = () => {
    if (interviewIdForReminder) {
      sendReminderMutation.mutate(interviewIdForReminder);
      setShowReminderConfirm(false);
      setInterviewIdForReminder(null);
    }
  };

  const cancelSendReminder = () => {
    setShowReminderConfirm(false);
    setInterviewIdForReminder(null);
  };

  const interviews = interviewsData?.items || EMPTY_ARRAY;
  const backendPagination = interviewsData?.pagination;

  const filteredInterviews = useMemo(() => {
    // Search and filtering are now handled by the backend
    let result = [...interviews];

    // Frontend sorting logic
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle case-insensitive string sorting
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();

        // Specific handling for dates if needed (already normalized strings in this case)
        if (sortConfig.key === 'date') {
          const dateA = new Date(`${a.date}T${a.time || '00:00'}`);
          const dateB = new Date(`${b.date}T${b.time || '00:00'}`);
          return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [interviews, sortConfig]);

  const totalItems = backendPagination?.totalInterviews || filteredInterviews.length;
  const totalPages = backendPagination?.totalPages || (totalItems === 0 ? 1 : Math.ceil(totalItems / pageSize));

  const paginatedInterviews = useMemo(() => {
    // With backend pagination, we don't slice locally.
    // If backend doesn't return pagination, fallback to slicing.
    if (backendPagination) {
      return filteredInterviews;
    }
    const startIndex = (page - 1) * pageSize;
    return filteredInterviews.slice(startIndex, startIndex + pageSize);
  }, [filteredInterviews, page, pageSize, backendPagination]);

  // Handle page overflow when filter/search changes
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [totalPages, page, setPage]);


  useEffect(() => {

  }, [user]);

  // Debounced search effect — write local input value to URL with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      updateInterviewFilter({ search: searchInputValue });
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchInputValue]);

  // Pagination effect
  useEffect(() => {
    // Page change handled by state, interviews from useQuery
  }, [page, pageSize]);


  const interviewTypes = [
    { id: "video", name: "Video Call", icon: Video },
    { id: "in_person", name: "In Person", icon: MapPin },
    // {id: "phone", name: "Phone Call", icon: Phone },
  ];


  const getRoundStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case "scheduled":   return "bg-blue-100 text-blue-800";
    case "completed":   return "bg-green-100 text-green-800";
    case "cancelled":   return "bg-red-100 text-red-800";
    default:            return "bg-gray-100 text-gray-800";
  }
};

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    updateInterviewSort(key, direction);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1 inline-block text-gray-400 group-hover:text-gray-600" />;
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="h-3 w-3 ml-1 inline-block text-brand" /> :
      <ArrowDown className="h-3 w-3 ml-1 inline-block text-brand" />;
  };

  const getTypeIcon = (type) => {
    const typeConfig = interviewTypes.find((t) => t.id === type);
    return typeConfig ? typeConfig.icon : Video;
  };

  const getTypeLabel = (type) => {
    const typeConfig = interviewTypes.find((t) => t.id === type);
    return typeConfig ? typeConfig.name : (type || 'Unknown');
  };

  // Helper function to filter input to only alphanumeric, space, and comma with character limit
  const filterAlphanumericSpaceComma = (value, maxLength = Infinity) => {
    const filtered = value.replace(/[^a-zA-Z0-9\s,]/g, '');
    return maxLength !== Infinity ? filtered.slice(0, maxLength) : filtered;
  };

  // Character limits for each field
  const FIELD_LIMITS = {
    candidate: 150,
    interviewer: 150,
    meetingLink: 300
  };


  const handleStatusChange = (interviewId, newStatus, additional = {}) => {
    if (newStatus === 'completed' && !additional.roundId) {
      setInterviewIdForRemark(interviewId);
      setRemarkOutcome('interviewed');
      setShowRemarkModal(true);
      return;
    }

    const payload = {
      id: interviewId,
      status: newStatus.toLowerCase(),
      iscompleted: newStatus.toLowerCase() === 'completed',
      ...additional
    };

    updateStatusMutation.mutate(payload);
  };

  const handleRemarkSubmit = async () => {
    if (!remarkContent.trim()) {
      toast.error('Please enter a remark');
      return;
    }

    const targetInt = (interviewsData?.items || []).find(i => i.id === interviewIdForRemark) || selectedInterview;
    const candidateObj = targetInt?.candidate;
    const candId = typeof candidateObj === 'object' ? (candidateObj?._id || candidateObj?.id) : (candidateObj || targetInt?.candidateId);
    const candName = targetInt?.candidateName || 'Candidate';

    // 1. Update Interview Status in database
    updateStatusMutation.mutate({
      id: interviewIdForRemark,
      status: 'completed',
      completionRemark: {
        remark: remarkContent,
        outcome: remarkOutcome,
        remarkedAt: new Date().toISOString(),
        remarkedBy: user?.id || user?._id,
        remarkerName: user?.fullName || user?.name || "HR Manager"
      }
    });

    // 2. Move Candidate to chosen pipeline stage (interviewed | documentation | rejected)
    if (candId) {
      try {
        const moveRes = await candidateService.moveCandidate(candId, remarkOutcome, true, remarkContent);
        if (moveRes && (moveRes.responseCode === 400 || moveRes.success === false)) {
          // Fallback to direct candidate status update
          await candidateService.updateCandidate(candId, { recruitmentStatus: remarkOutcome, status: remarkOutcome });
        }
        queryClient.invalidateQueries({ queryKey: ['candidates'] });
        queryClient.invalidateQueries({ queryKey: ['shortlisted-candidates'] });
        queryClient.invalidateQueries({ queryKey: ['candidate-pipeline'] });
        queryClient.invalidateQueries({ queryKey: ['candidate-stage-counts'] });
      } catch (err) {
        console.warn('Candidate stage update note:', err);
      }
    }

    if (remarkOutcome === 'interviewed') {
      toast.success(`Interview completed! Candidate ${candName} moved to Interviewed column in Hiring Pipeline.`, { duration: 5000 });
    } else if (remarkOutcome === 'documentation') {
      toast.success(`Interview completed! Candidate ${candName} moved to Documentation stage.`, { duration: 5000 });
    } else {
      toast.error(`Interview completed! Candidate ${candName} marked as Rejected.`, { duration: 4000 });
    }

    setShowRemarkModal(false);
    setRemarkContent('');
    setRemarkOutcome('interviewed');
    setShowInterviewDetails(false);
  };

  const handleDelete = async (interviewId) => {


    setInterviewToDelete(interviewId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (!interviewToDelete) return;
    deleteMutation.mutate(interviewToDelete);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setInterviewToDelete(null);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        const result = await interviewService.deleteInterview(id);
        if (result && (result.success || result.responseCode === 200)) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Set());
    if (successCount > 0) {
      toast.success(`${successCount} interview(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      refetchInterviews();
    } else {
      toast.error('Failed to delete selected interviews');
    }
  };

  const handleEditInterview = (interviewId) => {
    const interviewToEdit = interviews.find(i => i.id === interviewId);
    if (!interviewToEdit) return;

    const allRounds = interviewToEdit.rounds || [];
    const editableRounds = allRounds.filter(r => 
      !['completed', 'cancelled'].includes((r.status || "").toLowerCase())
    );

    if (allRounds.length === 0) {
      toast.error("No rounds found for this interview.");
      return;
    }

    if (allRounds.length === 1 && editableRounds.length === 1) {
      // Only one round and it's editable, proceed directly
      handleSelectRoundForEdit(interviewToEdit, allRounds[0]);
    } else {
      // Show picker with ALL rounds (modal UI will handle disabling)
      setRoundPickerData({
        interview: interviewToEdit,
        rounds: allRounds
      });
      setShowRoundPicker(true);
    }
  };

  const handleSelectRoundForEdit = (interview, round) => {
    const finalEditData = {
      ...interview,
      roundType: round.roundType,
      interviewer: round.interviewer,
      interviewerId: round.interviewer?.id || round.interviewer?._id,
      interviewerName: round.interviewer?.name,
      interviewerEmail: round.interviewer?.email,
      scheduledAt: round.scheduledAt,
      duration: round.duration,
      type: round.type || round.interviewType,
      location: round.location,
      meetingLink: round.meetingLink,
      notes: round.notes || round.extraNote,
    };
    setEditInterviewData(finalEditData);
    setShowScheduleForm(true);
    setShowRoundPicker(false);
  };

  // Granular permission-based access control (using PermissionGate component instead)


  const handleExportInterviews = () => {
    try {


      // Prepare data for export
      const exportData = interviews.map(interview => ({
        'Candidate Name': interview.candidateName,
        'Candidate Email': interview.candidateEmail,
        'Job Title': interview.jobTitle,
        'Interviewer Name': interview.interviewerName,
        'Interviewer Email': interview.interviewerEmail,
        'Date': interview.date,
        'Time': formatTimeAmPm(interview.time),
        'Duration': interview.duration,
        'Type': interview.type,
        'Location': interview.location,
        'Status': interview.status,
        'Created At': `${formatDate(interview.createdAt)} ${new Date(interview.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`,
        // 'Round': interview.round || 'N/A',
        'Notes': interview.notes || 'N/A'
      }));

      // Convert to CSV
      const csvContent = convertToCSV(exportData);

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `interviews_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);


      alert("Interviews exported successfully!");
    } catch (error) {

      alert("Error exporting interviews. Please try again.");
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return "";

    const headers = Object.keys(data[0]);
    const csvRows = [];

    // Add headers
    csvRows.push(headers.join(","));

    // Add data rows
    data.forEach((row) => {
      const values = headers.map((header) => {
        const value = row[header] || "";
        // Escape commas and quotes in values
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(","));
    });

    return csvRows.join('\n');
  };

  const clearFilters = () => {
    setSearchInputValue('');
    resetInterviewFilters();
    setSelectedRows(new Set());
  };

  // Selection handlers
  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = paginatedInterviews.map(i => i.id);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const mapInterviewsForExport = (interviews) => {
    return interviews.map(interview => ({
      'Candidate Name': interview.candidateName,
      'Candidate Email': interview.candidateEmail,
      'Job Title': interview.jobTitle,
      'Interviewer Name': interview.interviewerName,
      'Interviewer Email': interview.interviewerEmail,
      'Date': interview.date,
      'Time': formatTimeAmPm(interview.time),
      'Duration': interview.duration,
      'Type': interview.type,
      'Location': interview.location,
      'Status': interview.status,
      'Created At': `${formatDate(interview.createdAt)} ${new Date(interview.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}`,
      'Notes': interview.notes || 'N/A'
    }));
  };

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      interviewType: filterType !== 'all' ? filterType : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterStatus, filterType, dateRange.startDate, dateRange.endDate]);

  // Get current page interview IDs
  const currentPageInterviewIds = useMemo(
    () =>
      paginatedInterviews
        .map((i) => {
          const raw = i?._id ?? i?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [paginatedInterviews]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageInterviewIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'interview-scheduling',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? pageSize,
            sortBy: sortConfig.key,
            sortOrder: sortConfig.direction,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `interview-scheduling-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      page,
      pageSize,
      sortConfig.key,
      sortConfig.direction,
      currentPageInterviewIds,
    ]
  );

  // Handle export for selected interviews with format selection
  const handleInterviewSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected interviews to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleInterviewScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: pageSize,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageInterviewIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      interviews.length,
      baseExportFilters,
      requestBackendExport,
      pageSize,
      currentPageInterviewIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportCurrentPage = () => {
    if (!paginatedInterviews.length) {
      toast.error('No interviews to export on current page.');
      return;
    }
    const exportData = mapInterviewsForExport(paginatedInterviews);
    exportToCSV(exportData, 'interviews_page', {
      title: 'Interview Schedule',
      type: 'Current Page'
    });
    setShowExportModal(false);
    toast.success('Current page exported!');
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/recruitment/interviews/export?${queryParams.toString()}`;
      const filename = generateExportFilename('interviews_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('Interviews exported successfully!');
    } catch (error) {

      toast.error("Failed to export interviews");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/recruitment/interviews/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_interviews');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success('All interviews exported successfully!');
    } catch (error) {

      toast.error("Failed to export interviews");
    } finally {
      setExportLoading(false);
    }
  };


  const getInterviewsForDate = (date) => {
    return interviews.filter(interview => interview.date === date);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return "N/A";
    return parsed.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };



  // Error state with fallback
  if (interviewError && interviews.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0F2942] text-white flex items-center justify-center shadow-sm">
              <Users className="w-6 h-6 stroke-[2]" />
            </div>
            <div>
              <h1 className="text-[22px] font-bold text-[#0F2942] mb-0.5 leading-tight">
                {isEmployeeView ? "My Interviews" : "Interview Scheduling"}
              </h1>
              <p className="text-[12px] text-[#64748B]">
                {isEmployeeView ? "View and manage your scheduled interviews" : "Schedule and manage candidate interviews"}
              </p>
            </div>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            <PermissionGate permission="recruitment" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => setShowScheduleForm(true)}
                className="!bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white"
                disabled={loadingInterviews}
              >
                Schedule Interview
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Error Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-red-600 mb-4">
            <AlertCircle className="h-12 w-12 mx-auto mb-2" />
            <h3 className="text-lg font-semibold">Error Loading Interviews</h3>
            <p className="text-sm">{interviewError?.message || 'Unknown error'}</p>
          </div>
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => refetchInterviews()}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>

        {/* Empty State */}
        <div className="text-center py-12">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No interviews found
          </h3>
          <p className="text-gray-500 mb-4">
            Get started by scheduling your first interview.
          </p>
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setShowScheduleForm(true)}
              className="!bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white"
            >
              Schedule Interview
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-6 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0F2942] text-white flex items-center justify-center shadow-sm">
            <Users className="w-6 h-6 stroke-[2]" />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-[#0F2942] mb-0.5 leading-tight">
              {isEmployeeView ? "My Interviews" : "Interview Scheduling"}
            </h1>
            <p className="text-[12px] text-[#64748B]">
              {isEmployeeView ? "View and manage your scheduled interviews" : "Schedule and manage candidate interviews"}
            </p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1 mr-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-brand' : 'text-gray-500 hover:text-gray-700'}`}
              title="List View"
            >
              <List className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`p-2 rounded-md transition-all ${viewMode === 'calendar' ? 'bg-white shadow-sm text-brand' : 'text-gray-500 hover:text-gray-700'}`}
              title="Calendar View"
            >
              <Calendar className="h-4 w-4" />
            </button>
          </div>
          {selectedRows.size > 0 && (
            <PermissionGate permission="recruitment" action="delete">
              <Button
                variant="danger"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setShowBulkDeleteConfirm(true)}
                loading={bulkDeleteLoading}
              >
                Delete Selected ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="recruitment" action="read">
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected interviews`
                    : 'Export interviews'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleInterviewSelectedFormatExport}
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="recruitment" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setShowScheduleForm(true);
              }}
              className="!bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white"
              disabled={loadingInterviews}
            >
              Schedule Interview
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by candidate name or interviewer..."
              value={searchInputValue}
              maxLength={100}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                let value = e.target.value;
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                value = value.replace(/[^a-zA-Z0-9\s@._+-]/g, '');
                // Enforce max length
                if (value.length > 100) {
                  value = value.substring(0, 100);
                }

                setSearchInputValue(value);
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            {searchInputValue && (
              <button
                onClick={() => {

                  setSearchInputValue('');
                  // Maintain focus after clearing
                  setTimeout(() => {
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }, 50);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Types</option>
              <option value="video">Video Call</option>
              <option value="in_person">In Person</option>
            </select>
          </div>

          {/* Date Range Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={setDateRange}
              allowFutureDates={true}
            />
          </div>

          {/* Clear Filters */}
          {(searchInputValue || filterStatus !== 'all' || filterType !== 'all' || dateRange.startDate || dateRange.endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={clearFilters}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {currentDate.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
                    )
                  }
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Today
                </button>
                <button
                  onClick={() =>
                    setCurrentDate(
                      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
                    )
                  }
                  className="p-2 text-gray-400 hover:text-gray-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-7 gap-4">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 py-2"
                >
                  {day}
                </div>
              ))}
              {(() => {
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth();
                const firstDay = new Date(year, month, 1);
                const monthDays = new Date(year, month + 1, 0).getDate();
                const startOffset = (firstDay.getDay() + 6) % 7; // Monday=0
                const totalCells = Math.ceil((startOffset + monthDays) / 7) * 7;

                return Array.from({ length: totalCells }, (_, cellIndex) => {
                  if (cellIndex < startOffset) {
                    return (
                      <div
                        key={`empty-${cellIndex}`}
                        className="min-h-[120px] border border-dashed border-gray-100 rounded-lg p-2 bg-gray-50/50"
                      />
                    );
                  }

                  const day = cellIndex - startOffset + 1;
                  if (day > monthDays) {
                    return (
                      <div
                        key={`empty-${cellIndex}`}
                        className="min-h-[120px] border border-dashed border-gray-100 rounded-lg p-2 bg-gray-50/50"
                      />
                    );
                  }
                  const date = new Date(year, month, day);
                  const dateString = date.toISOString().split("T")[0];
                  const dayInterviews = getInterviewsForDate(dateString);

                  return (
                    <div
                      key={dateString}
                      className="min-h-[120px] border border-gray-200 rounded-lg p-2"
                    >
                      <div className="text-sm font-medium text-gray-900 mb-2">
                        {day}
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                        {dayInterviews.map((interview) => (
                          <div
                            key={interview.id}
                            className={`relative group rounded-lg border p-2 transition-all ${selectedRows.has(interview.id) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100'}`}
                          >
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={selectedRows.has(interview.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(interview.id);
                                }}
                                className="mt-1 h-3 w-3 text-brand focus:ring-0 border-gray-300 rounded cursor-pointer"
                              />
                              <div
                                onClick={() => {
                                  setSelectedInterview({
                                    ...interview,
                                    tempInternalNotes: interview.extraNote || '',
                                    onNotesChange: (val) => setInternalNotes(val)
                                  });
                                  setInternalNotes(interview.extraNote || '');
                                  setActiveRoundId(interview.roundId);
                                  setShowInterviewDetails(true);
                                }}
                                className="flex-1 cursor-pointer min-w-0"
                              >
                                <div className="text-xs font-semibold text-gray-900 truncate capitalize block">
                                  {interview.candidateName}
                                </div>
                                <div className="text-[10px] text-gray-500 truncate">{formatTimeAmPm(interview.time)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )
      }

      {/* Interviews List */}
      {
        viewMode === 'list' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                  <tr>
                    <th className="px-6 py-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={paginatedInterviews.length > 0 && selectedRows.size === paginatedInterviews.length}
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider w-[200px] cursor-pointer group hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('candidateName')}
                    >
                      <div className="flex items-center">
                        Candidate
                        {renderSortIcon('candidateName')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('interviewerName')}
                    >
                      <div className="flex items-center">
                        Interviewer
                        {renderSortIcon('interviewerName')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('date')}
                    >
                      <div className="flex items-center">
                        Date & Time
                        {renderSortIcon('date')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('type')}
                    >
                      <div className="flex items-center">
                        Type
                        {renderSortIcon('type')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {renderSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Created At
                        {renderSortIcon('createdAt')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingInterviews ? (
                    [...Array(5)].map((_, index) => (
                      <tr key={`skeleton-${index}`} className="animate-pulse">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-4"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-4 w-4 bg-gray-200 rounded mr-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end space-x-2">
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                            <div className="h-8 w-8 bg-gray-200 rounded"></div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    paginatedInterviews.map((interview) => (
                      <tr key={interview.id} className={`hover:bg-blue-50/30 ${selectedRows.has(interview.id) ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(interview.id)}
                            onChange={() => handleSelectRow(interview.id)}
                            className="h-4 w-4 text-brand focus:ring-0 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-6 py-4 overflow-hidden w-[200px] max-w-[200px]">
                          <div className="min-w-0 w-full">
                            <button
                              onClick={() => navigate(`/view_candidate/${interview.candidate?.id}`)}
                              className="text-sm font-medium text-[#0F2942] hover:text-blue-800 truncate block capitalize cursor-pointer"
                              title={interview.candidate?.name}
                            >
                              {interview.candidate?.name}
                            </button>
                            <div
                              className="text-sm text-gray-500 truncate block capitalize"
                              title={interview.job?.title}
                            >
                              {interview.job?.title}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                          <div className="max-w-[200px] truncate" title={interview.interviewerName}>
                            {interview.interviewerName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {(interview.rounds || []).length > 0 ? (
                              (interview.rounds || []).map((round, idx) => (
                                <div key={round.id || idx} className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">
                                    R{idx + 1}
                                  </span>
                                  <div className="text-sm text-gray-900">
                                    {formatDate(round.scheduledAt)} {round.scheduledAt
                                      ? new Date(round.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                                      : 'N/A'}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-gray-900">
                                {formatDate(interview.scheduledAt)} {interview.scheduledAt
                                  ? new Date(interview.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                                  : 'N/A'}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {React.createElement(getTypeIcon(interview.type), {
                              className: "h-4 w-4 text-gray-400 mr-2",
                            })}
                            <span className="text-sm text-gray-900 capitalize">
                              {getTypeLabel(interview.type)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {(interview.rounds || []).length > 0 ? (
                              (interview.rounds || []).map((round, idx) => (
                                <div key={round.id || idx} className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-semibold text-gray-400 whitespace-nowrap">
                                    R{idx + 1}
                                  </span>
                                  <span
                                    className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${getRoundStatusColor(round.status)}`}
                                  >
                                    {(round.status || 'Scheduled').toLowerCase()}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getRoundStatusColor(interview.status)}`}>
                                {interview.status}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="text-sm text-gray-900">
                            {formatDate(interview.createdAt)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(interview.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {(isEmployeeView || user?.hasGranularPermission?.('recruitment', 'read')) ? (
                              <button
                                onClick={() => navigate(`/view_interview/${interview.id}`)}
                                className="text-[#0F2942] hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            ) : (
                              <PermissionGate permission="recruitment" action="read">
                                <button
                                  onClick={() => navigate(`/view_interview/${interview.id}`)}
                                  className="text-[#0F2942] hover:text-blue-900"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                            {['scheduled', 'rescheduled'].includes(interview.status) && (
                              (isEmployeeView || user?.hasGranularPermission?.('recruitment', 'update')) ? (
                                <button
                                  onClick={() => handleSendReminder(interview.id)}
                                  className={`text-amber-600 hover:text-amber-900 ${(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  title="Send Reminder"
                                  disabled={(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id}
                                >
                                  {(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Send className="h-4 w-4" />
                                  )}
                                </button>
                              ) : (
                                <PermissionGate permission="recruitment" action="update">
                                  <button
                                    onClick={() => handleSendReminder(interview.id)}
                                    className={`text-amber-600 hover:text-amber-900 ${(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    title="Send Reminder"
                                    disabled={(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id}
                                  >
                                    {(sendReminderMutation.isLoading || sendReminderMutation.isPending) && sendReminderMutation.variables === interview.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Send className="h-4 w-4" />
                                    )}
                                  </button>
                                </PermissionGate>
                              )
                            )}
                            {interview.status !== 'completed' && (
                              <>
                                {['scheduled', 'rescheduled', 'in progress'].includes(interview.status?.toLowerCase()) && (
                                  (isEmployeeView || user?.hasGranularPermission?.('recruitment', 'update')) ? (
                                    <button
                                      onClick={() => handleStatusChange(interview.id, 'completed')}
                                      className="text-green-600 hover:text-green-900"
                                      title="Mark Completed"
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <PermissionGate permission="recruitment" action="update">
                                      <button
                                        onClick={() => handleStatusChange(interview.id, 'completed')}
                                        className="text-green-600 hover:text-green-900"
                                        title="Mark Completed"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </button>
                                    </PermissionGate>
                                  )
                                )}
                                <PermissionGate permission="recruitment" action="update">
                                  <button
                                    onClick={() => handleEditInterview(interview.id)}
                                    className="text-brand hover:text-brand-dark"
                                    title="Edit"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              </>
                            )}
                            <PermissionGate permission="recruitment" action="delete">
                              <button
                                onClick={() => handleDelete(interview.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {!loadingInterviews && (
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                limit={pageSize}
                hasNextPage={page < totalPages}
                hasPrevPage={page > 1}
                onPageChange={(p) => setPage(p)}
                onLimitChange={(l) => { setPageSize(Number(l)); setPage(1); }}
                border={false}
              />
            )}
          </div>
        )
      }

      {/* Empty State */}
      {
        !loadingInterviews && interviews.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No interviews found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchInputValue || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by scheduling your first interview.'
              }
            </p>
            {!searchInputValue && filterStatus === 'all' && (
              <PermissionGate permission="recruitment" action="create">
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowScheduleForm(true)}
                  className="!bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white"
                >
                  Schedule Interview
                </Button>
              </PermissionGate>
            )}
          </div>
        )
      }

      {/* Modals */}
      <ScheduleInterviewModal
        isOpen={showScheduleForm}
        onClose={() => {
          setShowScheduleForm(false);
          setEditInterviewData(null);
        }}
        onSuccess={(result) => {
          queryClient.invalidateQueries({ queryKey: ['interviews'] });
        }}
        candidates={candidates}
        editData={editInterviewData}
      />


      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Interview
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to delete this interview? This action
                  cannot be undone.
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={cancelDelete}
                    className="flex-1"
                    disabled={deleteMutation.isLoading}
                  >
                    No
                  </Button>
                  <PermissionGate permission="recruitment" action="delete">
                    <Button
                      variant="danger"
                      onClick={confirmDelete}
                      className="flex-1"
                      disabled={deleteMutation.isLoading}
                    >
                      {deleteMutation.isLoading ? "Deleting..." : "Yes"}
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Delete Selected Interviews
                </h3>
                <p className="text-gray-600 text-center mb-2">
                  You are about to permanently delete{' '}
                  <span className="font-semibold text-red-600">{selectedRows.size} interview(s)</span>.
                </p>
                <p className="text-sm text-gray-500 text-center mb-6">
                  This action cannot be undone.
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowBulkDeleteConfirm(false)}
                    className="flex-1"
                    disabled={bulkDeleteLoading}
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="recruitment" action="delete">
                    <Button
                      variant="danger"
                      onClick={handleBulkDelete}
                      className="flex-1"
                      loading={bulkDeleteLoading}
                    >
                      Delete {selectedRows.size} Interview(s)
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Interviews"
        showDateRange
        scopedFormatExport={{
          onExport: handleInterviewScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Reminder Confirmation Modal */}
      {
        showReminderConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4 !mt-0">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full">
                  <Send className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Send Interview Reminder
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Are you sure you want to send a reminder email to the candidate and interviewer for this interview?
                </p>
                <div className="flex space-x-3">
                  <Button
                    variant="secondary"
                    onClick={cancelSendReminder}
                    className="flex-1"
                    disabled={sendReminderMutation.isLoading || sendReminderMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={confirmSendReminder}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    disabled={sendReminderMutation.isLoading || sendReminderMutation.isPending}
                  >
                    {sendReminderMutation.isLoading || sendReminderMutation.isPending ? "Sending..." : "Yes, Send"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Complete Interview Remarks Modal */}
      {
        showRemarkModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4 !mt-0 animate-in fade-in duration-200">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100">
              {/* Modal Header */}
              <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-white/10 text-emerald-400">
                    <CheckCircle className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-white">Complete Interview & Add Remarks</h3>
                    <p className="text-xs text-slate-300">Enter interview feedback and completion notes</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRemarkModal(false)}
                  className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5 text-xs">
                {/* Target Candidate Info Summary */}
                {(() => {
                  const targetInt = (interviewsData?.items || []).find(i => i.id === interviewIdForRemark);
                  return targetInt ? (
                    <div className="p-3.5 bg-slate-50 rounded-2xl border border-gray-200 flex items-center justify-between gap-3">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Candidate</span>
                        <p className="font-extrabold text-sm text-gray-900">{targetInt.candidateName}</p>
                        <p className="text-gray-500 text-[11px]">{targetInt.jobTitle} • {targetInt.candidateEmail || 'No email'}</p>
                      </div>
                      <span className="px-2.5 py-1 rounded-full bg-blue-50 text-brand font-bold text-[11px] border border-blue-100">
                        Interview Completed
                      </span>
                    </div>
                  ) : null;
                })()}

                {/* Feedback & Remarks Textarea */}
                <div className="space-y-1.5">
                  <label className="block font-extrabold text-gray-800 text-xs">
                    Interview Remarks & Feedback *
                  </label>
                  <div className="relative">
                    <textarea
                      value={remarkContent}
                      onChange={(e) => {
                        const filteredValue = e.target.value.replace(/[^a-zA-Z0-9\s.,?!@#$%^&*()-_+:=;'"/[\]{}]/g, '');
                        if (filteredValue.length <= 500) {
                          setRemarkContent(filteredValue);
                        }
                      }}
                      placeholder="e.g. Interview completed successfully - Good candidate background and technical skills."
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-brand/20 min-h-[100px]"
                      rows={4}
                    />
                    <div className="flex justify-end mt-1 text-[11px] text-gray-400">
                      {remarkContent.length}/500
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setShowRemarkModal(false)}
                    className="px-4 py-2 text-xs rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleRemarkSubmit}
                    className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-md"
                    disabled={updateStatusMutation.isLoading || updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isLoading || updateStatusMutation.isPending
                      ? "Submitting..."
                      : "Submit Remarks & Complete Interview"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Round Picker Modal */}
      {showRoundPicker && roundPickerData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h3 className="text-base font-bold text-gray-900">Select Round to Edit</h3>
              <button
                onClick={() => setShowRoundPicker(false)}
                className="p-2 hover:bg-white rounded-full transition-colors group"
              >
                <XCircle className="h-5 w-5 text-gray-400 group-hover:text-red-500" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-6">
                This candidate has multiple active interview rounds. Please select the round you wish to modify:
              </p>
              <div className="space-y-3">
                {roundPickerData.rounds.map((round, index) => {
                  const isFinalized = ['completed', 'cancelled'].includes((round.status || '').toLowerCase());
                  return (
                    <button
                      key={round.id || index}
                      onClick={() => !isFinalized && handleSelectRoundForEdit(roundPickerData.interview, round)}
                      disabled={isFinalized}
                      className={`w-full p-4 border rounded-xl text-left transition-all group relative overflow-hidden ${
                        isFinalized 
                          ? 'bg-gray-50 border-gray-100 opacity-60 cursor-not-allowed' 
                          : 'border-gray-200 hover:border-brand hover:bg-brand/[0.02] hover:shadow-sm'
                      }`}
                    >
                      {!isFinalized && <div className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-brand transition-colors" />}
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`capitalize text-sm font-bold transition-colors ${isFinalized ? 'text-gray-400' : 'text-gray-900 group-hover:text-brand'}`}>
                            {round.roundType}
                          </span>
                          {isFinalized && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${
                              round.status?.toLowerCase() === 'completed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {round.status}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          Round {index + 1}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(round.scheduledAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(round.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <button
                  onClick={() => setShowRoundPicker(false)}
                  className="w-full py-3 text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default InterviewScheduling;
